import React, {useCallback, useEffect, useState, useRef} from 'react';
import cx from 'classnames';
import {observer} from 'mobx-react';
import {noop} from '@priority-software/priority-style-react';
import {formsStore} from '../../../stores/Form/FormsStore';
import {ScreenHeader} from './ScreenHeader/ScreenHeader';
import {SubForms} from './SubForms/SubForms';
import {apiLocker} from '../../../stores/Form/API_Locker';
import {ScreenLocker} from './ScreenLocker';
import {SelectListModalRefType, SelectListModal} from './SelectListModal';
import {listTypes} from './SelectListModal/enum';
import {Form} from './Form';
import {NewRecordDialog} from './NewRecordDialog';
import {useRecordDialog} from './NewRecordDialog/useRecordDialog';
import {SuspendedJobTypes} from '../consts/suspendedJobTypes';
import {createPortal} from 'react-dom';
import {scrollToViewStart} from '../utils/scrollToViewStart';
import {moveUp} from '../actions/screen';
import styles from '../styles/screen.module.scss';
import {createDataTestId} from '../../../utils/dataTestId';
import {getServerTexts} from './AdvancedSearch/AdvancedSearchDrawer/texts';

export const Screen = observer(() => {
    const modalRef = useRef<SelectListModalRefType>(null);
    const screenBodyRef = useRef<HTMLDivElement>(null);
    const [textsLoaded, setTextsLoaded] = useState(false);
    const {currentForm, currentScreen} = formsStore;
    const activeLevel = currentScreen?.activeLevel;
    const session = currentScreen?.session;
    const {isRecordDialogOpen, openNewEmptyRecord, hideEmptyRecord} = useRecordDialog();

    const currScreenLockerData = apiLocker.getDataBySession(currentForm?.session);

    const onBreadCrumbClick = async (level: number, session: string) => {
        await moveUp(level, session);
    };

    const runSubForm = useCallback(
        async (subFormName: string, parentLevel: number, isHTMLForm?: boolean) => {
            const parentForm = formsStore.getFormByLevel(formsStore.currentSession, parentLevel);

            formsStore.setEventForRecordingMacro({
                type: SuspendedJobTypes.SubForm,
                formName: subFormName,
                parentForm: parentForm
            });
            await formsStore.currentScreen.runSubForm(subFormName, parentForm, isHTMLForm);
        },
        []
    );

    const openSelectListModal = useCallback((listType: listTypes) => {
        modalRef.current.openSelectListModal(listType);
    }, []);

    const disabledSubFormList = currentForm?.searchModeLevel
        ? true
        : !currentForm?.records[currentForm.currentRow]?.keys;

    useEffect(() => {
        (async () => {
            setTextsLoaded(false);
            await getServerTexts();
            setTextsLoaded(true);
        })();
    }, []);

    useEffect(
        function scrollFormIntoViewport() {
            // Scroll active form into the viewport start
            const scrollElement = screenBodyRef.current;

            if (session && scrollElement) {
                const activeForm = formsStore.getFormByLevel(session, activeLevel);
                if (activeForm) {
                    scrollToViewStart(`#${activeForm.id}`, scrollElement);
                }
            }
        },
        [activeLevel, session]
    );

    const isApiLock = currentForm?.isApiLock || noop;

    const renderForm = () => {
        if (!currentForm) return null;

        return (
            <div className={styles.screenBody} ref={screenBodyRef}>
                <div id={currentForm.id} {...createDataTestId(`main-form-wrapper`, false)}>
                    <Form
                        hideNewEmptyRecord={hideEmptyRecord}
                        currentForm={currentForm}
                        isApiLock={isApiLock}
                        openSelectListModal={openSelectListModal}
                        componentClasses={styles.sectionIndentX}
                        openNewEmptyRecord={openNewEmptyRecord}
                    />
                </div>

                <div className={styles.subformSection}>
                    <SubForms
                        disabledSubformList={disabledSubFormList}
                        subForm={currentForm.activeTabSubForm}
                        subFormsList={currentForm.subFormsList}
                        isApiLock={isApiLock}
                        openSelectListModal={openSelectListModal}
                        runSubForm={runSubForm}
                        parentLevel={currentForm.level}
                    />
                </div>
            </div>
        );
    };

    if (currentForm && textsLoaded) {
        if (currentScreen.standAlone) {
            return (
                <ScreenLocker
                    locked={currScreenLockerData?.locked}
                    showLoader={false}
                    toggleLoader={currScreenLockerData?.toggleLoader}
                />
            );
        }

        return (
            <div key={currentForm.session} className={styles.screenInner}>
                <ScreenHeader
                    componentClasses={styles.sectionIndentX}
                    session={currentForm.session}
                    breadcrumbs={currentScreen.breadcrumbs}
                    onBreadCrumbClick={onBreadCrumbClick}
                />

                {renderForm()}

                <ScreenLocker
                    locked={currScreenLockerData?.locked}
                    showLoader={currScreenLockerData?.showLoader}
                    toggleLoader={currScreenLockerData?.toggleLoader}
                />

                <SelectListModal ref={modalRef} isApiLock={isApiLock} />

                {isRecordDialogOpen &&
                    currentScreen.isRecordDialog &&
                    createPortal(
                        <div className={cx('reactComp', styles.screenInner)}>
                            <NewRecordDialog
                                currentForm={currentForm}
                                closeRecordDialog={hideEmptyRecord}
                                runSubForm={runSubForm}
                                disabledSubformList={disabledSubFormList}
                                isApiLock={isApiLock}
                            />
                        </div>,
                        document.body
                    )}
            </div>
        );
    }

    return null;
});
